import React, { createContext, useState, useContext, useCallback, ReactNode, useRef, useEffect, useMemo } from 'react';
import { gql, useLazyQuery, ApolloError } from '@apollo/client';
import dayjs from 'dayjs';
import { 
    KubernetesPageLoadPayload, 
    KubernetesFilterOptionsPayload as KubernetesFilterOptions, // Renaming for consistency
    KubernetesAnalysisFiltersInput,
} from './KubernetesAnalysis';

// --- GraphQL Queries ---
const GET_KUBERNETES_FILTER_OPTIONS = gql`
  query GetKubernetesFilterOptions($tenantId: ID!, $dateRange: TimeRangeInput!) {
    getKubernetesFilterOptions(input: { tenantId: $tenantId, dateRange: $dateRange }) {
      clusters, projects, namespaces, labelKeys, dataSources { id, name, providerType, isSyncing }
    }
  }
`;
const GET_KUBERNETES_PAGE_LOAD_DATA = gql`
  query GetKubernetesPageLoadData($input: KubernetesPageLoadInput!) {
    getKubernetesPageLoadData(input: $input) {
      kpis { totalCost, totalCostChangePercentage, forecastedCost, forecastedCostChangePercentage, storageCost, cpuCost, memoryCost },
      costOverTime { date, groups { name, cost } }
    }
  }
`;

// --- Type Definitions ---
interface KubernetesPageLoadInput { tenantId: string; dateRange: { start: string; end: string; }; filters: KubernetesAnalysisFiltersInput | null; }
interface GetK8sFilterOptionsData { getKubernetesFilterOptions: KubernetesFilterOptions; }
interface GetK8sPageLoadDataVars { input: KubernetesPageLoadInput; }

// --- Context Definition ---
interface IKubernetesAnalysisContext {
  isLoading: boolean;
  isLoaded: boolean;
  error: ApolloError | null;
  filterOptions: KubernetesFilterOptions | null;
  initialK8sData: KubernetesPageLoadPayload | null;
  preloadedQueryVariables: KubernetesPageLoadInput | null;
  preloadK8sData: (tenantId: string) => void;
  clearK8sPreloadedData: () => void;
}

const KubernetesAnalysisContext = createContext<IKubernetesAnalysisContext>({
  isLoading: false, isLoaded: false, error: null, filterOptions: null, initialK8sData: null,
  preloadedQueryVariables: null,
  preloadK8sData: () => console.warn('K8s Provider not initialized'),
  clearK8sPreloadedData: () => console.warn('K8s Provider not initialized'),
});

export const useKubernetesAnalysisContext = () => useContext(KubernetesAnalysisContext);

// --- Provider Component ---
export const KubernetesAnalysisProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<ApolloError | null>(null);
  const [filterOptions, setFilterOptions] = useState<KubernetesFilterOptions | null>(null);
  const [initialK8sData, setInitialK8sData] = useState<KubernetesPageLoadPayload | null>(null);
  const [preloadedQueryVariables, setPreloadedQueryVariables] = useState<KubernetesPageLoadInput | null>(null);

  const [fetchFilterOptions] = useLazyQuery<GetK8sFilterOptionsData>(GET_KUBERNETES_FILTER_OPTIONS, { fetchPolicy: 'network-only' });
  const [fetchInitialData] = useLazyQuery<{ getKubernetesPageLoadData: KubernetesPageLoadPayload }, GetK8sPageLoadDataVars>(GET_KUBERNETES_PAGE_LOAD_DATA, { fetchPolicy: 'network-only' });

  const stateRef = useRef({ isLoading, isLoaded });
  useEffect(() => { stateRef.current = { isLoading, isLoaded }; }, [isLoading, isLoaded]);

  const preloadK8sData = useCallback(async (tenantId: string) => {
    if (stateRef.current.isLoading || stateRef.current.isLoaded || !tenantId) return;

    console.log(`[K8sContext] Preloading data for tenant: ${tenantId}`);
    setIsLoading(true);
    setError(null);

    try {
      const { data: filterOptionsData, error: filterOptionsError } = await fetchFilterOptions({
        variables: { tenantId, dateRange: { start: dayjs().subtract(29, 'day').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') } },
      });
      if (filterOptionsError) throw filterOptionsError;
      const fetchedFilterOptions = filterOptionsData?.getKubernetesFilterOptions;
      if (!fetchedFilterOptions) throw new Error("Received no data for K8s filter options.");
      setFilterOptions(fetchedFilterOptions);

      const LOCAL_STORAGE_KEY = `k8sAnalysisFilters_${tenantId}`;
      const savedFiltersJSON = localStorage.getItem(LOCAL_STORAGE_KEY);
      let queryVariables: KubernetesPageLoadInput;

      try {
        if (!savedFiltersJSON) throw new Error("No saved filters.");
        const savedState = JSON.parse(savedFiltersJSON);
        const validDataSources = new Map(fetchedFilterOptions.dataSources.map(ds => [ds.id, ds]));
        const reconciledDataSources = (savedState.selectedDataSources || []).filter((id: string) => {
            const ds = validDataSources.get(id);
            return ds && !ds.isSyncing;
        });
        const formattedLabels = (savedState.labelFilters || []).flatMap((pair: any) => pair.values.map((val: string) => ({ key: pair.key, value: val })));
        
        queryVariables = {
            tenantId,
            dateRange: {
                start: savedState.customStartDate ? dayjs(savedState.customStartDate).format('YYYY-MM-DD') : dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
                end: savedState.customEndDate ? dayjs(savedState.customEndDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
            },
            filters: {
                dataSourceIds: reconciledDataSources.length > 0 ? reconciledDataSources : null,
                clusters: (savedState.selectedClusters || []).length > 0 ? savedState.selectedClusters : null,
                projectIds: (savedState.selectedProjects || []).length > 0 ? savedState.selectedProjects : null,
                namespaces: (savedState.selectedNamespaces || []).length > 0 ? savedState.selectedNamespaces : null,
                labels: formattedLabels.length > 0 ? formattedLabels : null,
            },
        };
        console.log("[K8sContext] Successfully built query variables from localStorage.");
      } catch (e) {
        console.log("[K8sContext] No/invalid saved state found, using default filters for preload.");
        const defaultSelectableSources = fetchedFilterOptions.dataSources.filter(ds => !ds.isSyncing).map(ds => ds.id);
        queryVariables = {
            tenantId,
            dateRange: { start: dayjs().subtract(29, 'day').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') },
            filters: { dataSourceIds: defaultSelectableSources.length > 0 ? defaultSelectableSources : null },
        };
      }
      
      setPreloadedQueryVariables(queryVariables);
      
      const { data: pageLoadData, error: pageLoadError } = await fetchInitialData({ variables: { input: queryVariables } });
      if (pageLoadError) throw pageLoadError;

      setInitialK8sData(pageLoadData?.getKubernetesPageLoadData ?? null);
      if (pageLoadData) {
        setIsLoaded(true);
      }
    } catch (e: any) {
      console.error("Failed to preload K8s data", e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [fetchFilterOptions, fetchInitialData]);

  const clearK8sPreloadedData = useCallback(() => {
    console.log('[K8sContext] Clearing preloaded data.');
    setIsLoaded(false); setIsLoading(false); setFilterOptions(null);
    setInitialK8sData(null); setPreloadedQueryVariables(null); setError(null);
  }, []);

  const value = useMemo(() => ({
    isLoading, isLoaded, error, filterOptions, initialK8sData: initialK8sData,
    preloadedQueryVariables: preloadedQueryVariables, preloadK8sData, clearK8sPreloadedData,
  }), [
    isLoading, isLoaded, error, filterOptions, initialK8sData,
    preloadedQueryVariables, preloadK8sData, clearK8sPreloadedData
  ]);

  return ( <KubernetesAnalysisContext.Provider value={value}>{children}</KubernetesAnalysisContext.Provider> );
};